import { PaginationOptions } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import { keepPreviousData, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PaginatedResponse, PaginationInformation } from '../types/api';

type PaginatedGenericUseQueryOptions<
  TData,
  TArgs,
  TError = Error,
  TSelect = PaginatedResponse<TData>,
> = Omit<
  UseQueryOptions<PaginatedResponse<TData>, TError, TSelect, readonly unknown[]>,
  'queryFn'
> & {
  queryFn: (args: TArgs, paginationOpts: PaginationOptions) => Promise<PaginatedResponse<TData>>;
  pageSize?: number;
};

export function usePaginatedQuery<TData, TArgs>(
  args: TArgs,
  options: PaginatedGenericUseQueryOptions<TData, TArgs>,
) {
  const [page, setPage] = useState(PaginationOptions.INITIAL_PAGE);

  const paginationOpts = useMemo(
    () => new PaginationOptions({ page, pageSize: options.pageSize }),
    [page, options.pageSize],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to reset the page to the default when args change
  useEffect(() => {
    // Reset to first page when args change
    setPage(PaginationOptions.INITIAL_PAGE);
  }, [args]);

  const { data: queryData, ...queryRest } = useQuery({
    ...options,
    queryKey: [...options.queryKey, paginationOpts],
    queryFn: () => options.queryFn(args, paginationOpts),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    // If we're not on the first page and the page is empty, go back a page
    // This usually occurs when deleting all items in the last page
    if (queryData?.data.length === 0 && page !== 1 && !queryRest.isFetching) {
      setPage((page) => page - 1);
    }
  }, [page, queryData?.data.length, queryRest.isFetching]);

  const paginationResponse = useMemo<Pick<
    PaginationInformation,
    'total' | 'total_pages' | 'per_page'
  > | null>(() => {
    if (!queryData) {
      return null;
    }

    return {
      total: queryData.pagination.total,
      total_pages: queryData.pagination.total_pages,
      per_page: queryData.pagination.per_page,
    };
  }, [queryData?.pagination, queryData]);

  const hasNextPage = useMemo<boolean>(
    () => !!paginationResponse && page < paginationResponse.total_pages,
    [page, paginationResponse],
  );
  const hasPreviousPage = useMemo<boolean>(
    () => !!paginationResponse && page > 1,
    [page, paginationResponse],
  );

  const nextPage = useCallback(() => setPage((p) => (hasNextPage ? p + 1 : p)), [hasNextPage]);
  const previousPage = useCallback(
    () => setPage((p) => (hasPreviousPage ? p - 1 : p)),
    [hasPreviousPage],
  );

  return {
    ...queryRest,
    data: queryData?.data,
    page,
    nextPage,
    previousPage,
    hasNextPage,
    hasPreviousPage,
    pagination: paginationResponse,
  };
}
